<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  sortOrder: {
    type: String,
    default: wootConstants.INBOX_SORT_BY.NEWEST,
  },
});

const emit = defineEmits(['update:sort-order']);

const { t } = useI18n();
const showSortMenu = ref(false);
const sortButton = ref(null);
const sortMenu = ref(null);
const sortMenuStyle = ref({});

const sortOptions = [
  {
    key: wootConstants.INBOX_SORT_BY.NEWEST,
    label: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.NEWEST'),
    icon: 'i-lucide-arrow-down',
  },
  {
    key: wootConstants.INBOX_SORT_BY.OLDEST,
    label: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.OLDEST'),
    icon: 'i-lucide-arrow-up',
  },
];

const activeSortLabel = () =>
  sortOptions.find(option => option.key === props.sortOrder)?.label ||
  sortOptions[0].label;

const updateSortMenuPosition = () => {
  const button = sortButton.value;
  if (!button) return;

  const buttonRect = button.getBoundingClientRect();
  const viewportPadding = 16;
  const menuWidth = Math.min(280, window.innerWidth - viewportPadding * 2);
  const preferredLeft =
    window.innerWidth < 768 ? buttonRect.right - menuWidth : buttonRect.left;
  const left = Math.min(
    Math.max(preferredLeft, viewportPadding),
    window.innerWidth - menuWidth - viewportPadding
  );

  sortMenuStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `calc(${buttonRect.bottom}px + var(--acl-inbox-sort-menu-gap))`,
  };
};

const toggleSortMenu = async () => {
  if (showSortMenu.value) {
    showSortMenu.value = false;
    return;
  }

  updateSortMenuPosition();
  showSortMenu.value = true;
  await nextTick();
  updateSortMenuPosition();
};

const closeSortMenuOnOutsidePointer = event => {
  if (!showSortMenu.value) return;
  if (sortButton.value?.contains(event.target)) return;
  if (sortMenu.value?.contains(event.target)) return;
  showSortMenu.value = false;
};

const selectSort = key => {
  showSortMenu.value = false;
  if (key === props.sortOrder) return;
  emit('update:sort-order', key);
};

onMounted(() => {
  document.addEventListener('pointerdown', closeSortMenuOnOutsidePointer);
  window.addEventListener('resize', updateSortMenuPosition);
  window.addEventListener('scroll', updateSortMenuPosition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeSortMenuOnOutsidePointer);
  window.removeEventListener('resize', updateSortMenuPosition);
  window.removeEventListener('scroll', updateSortMenuPosition, true);
});
</script>

<template>
  <div class="acl-inbox-sort-wrap flex items-center justify-end">
    <button
      ref="sortButton"
      type="button"
      class="acl-inbox-sort-button"
      :class="{ 'acl-inbox-sort-button--active': showSortMenu }"
      :aria-label="t('INBOX.DISPLAY_MENU.SORT')"
      :aria-expanded="showSortMenu"
      aria-haspopup="menu"
      @click="toggleSortMenu"
    >
      <span class="i-lucide-arrow-down-narrow-wide" aria-hidden="true" />
    </button>
  </div>

  <Teleport to="body">
    <div
      v-if="showSortMenu"
      ref="sortMenu"
      class="acl-inbox-sort-menu acl-inbox-sort-menu--teleported [--acl-inbox-sort-menu-gap:8px] md:[--acl-inbox-sort-menu-gap:24px]"
      :style="sortMenuStyle"
      role="menu"
      @pointerdown.stop
    >
      <button
        v-for="option in sortOptions"
        :key="option.key"
        type="button"
        role="menuitemradio"
        class="acl-inbox-sort-option"
        :class="{
          'acl-inbox-sort-option--active': sortOrder === option.key,
        }"
        :aria-checked="sortOrder === option.key"
        :title="activeSortLabel()"
        @click="selectSort(option.key)"
      >
        <span :class="option.icon" aria-hidden="true" />
        <span>{{ option.label }}</span>
        <span
          v-if="sortOrder === option.key"
          class="i-lucide-check"
          aria-hidden="true"
        />
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.acl-inbox-sort-wrap {
  position: relative;
  flex-shrink: 0;
}

.acl-inbox-sort-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #525252;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 50%;
  transition:
    background-color 0.12s,
    color 0.12s;
}

.acl-inbox-sort-button:hover {
  color: #0a0a0a;
  background: rgba(10, 10, 10, 0.06);
}

.acl-inbox-sort-button--active,
.acl-inbox-sort-button--active:hover {
  color: var(--acl-inbox-sort-active-color, #15603e);
  background: var(--acl-inbox-sort-active-background, rgba(21, 96, 62, 0.12));
}

.acl-inbox-sort-button > span {
  width: 24px;
  height: 24px;
}

.acl-inbox-sort-menu {
  z-index: 140;
  width: 280px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
}

.acl-inbox-sort-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  color: #343a40;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.acl-inbox-sort-option > span:first-child {
  width: 20px;
  height: 20px;
  color: #626c76;
  background-color: currentColor;
  flex-shrink: 0;
}

.acl-inbox-sort-option > span:nth-child(2) {
  flex: 1;
  white-space: nowrap;
}

.acl-inbox-sort-option:hover {
  background-color: #f8fafc;
  border-color: #d7dce2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
}

.acl-inbox-sort-option--active {
  color: #27332b;
  background-color: #f3faf5;
  border-color: #b8d8c3;
}

.acl-inbox-sort-option > .i-lucide-check {
  width: 18px;
  height: 18px;
  margin-left: auto;
  color: #15603e;
  background-color: currentColor;
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .acl-inbox-sort-button:not(.acl-inbox-sort-button--active) {
    width: 32px;
    height: 32px;
    color: #52606d;
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .acl-inbox-sort-button:not(.acl-inbox-sort-button--active):hover {
    background: rgba(15, 23, 42, 0.05);
  }

  .acl-inbox-sort-button > span {
    width: 22px;
    height: 22px;
  }
}

:deep(.dark) .acl-inbox-sort-button {
  color: #a1a1aa;
}

:deep(.dark) .acl-inbox-sort-button:hover {
  color: #e5e5e5;
  background: rgba(229, 229, 229, 0.08);
}

:deep(.dark) .acl-inbox-sort-button--active,
:deep(.dark) .acl-inbox-sort-button--active:hover {
  color: #86efac;
  background: rgba(21, 96, 62, 0.25);
}

:deep(.dark) .acl-inbox-sort-menu {
  background: #1f2123;
  border-color: #2c2f33;
}

:deep(.dark) .acl-inbox-sort-option {
  background: #202326;
  border-color: #34383d;
  color: #e5e5e5;
}

:deep(.dark) .acl-inbox-sort-option:hover {
  background: #282c30;
  border-color: #4a5158;
}

:deep(.dark) .acl-inbox-sort-option--active {
  background: #1e3326;
  border-color: #3d7650;
}
</style>
